import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input, OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { bindFunctionsToThis, isNullOrUndefined } from '../../utils/object.extensions';

@Component({
  selector: 'ig-select',
  templateUrl: './select.field.component.html',
  styleUrls: ['./select.field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectFieldComponent,
      multi: true
    }
  ],
})
export class SelectFieldComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {

  private _value: { text: string, value: string | number, selected: boolean }[];
  private _notifyChange: any;

  public isShowing = false;
  public formGroup: FormGroup;
  public fieldValue: string;
  @Input() public label: string;
  @Input() public disabled: boolean;
  @Input() place: 'top' | 'bottom' = 'bottom';
  @Input() align: 'left' | 'right' = 'left';
  @Input() icon = 'expand_more';
  @Input() public showDropDownItem: string;

  @Output() itemSelected = new EventEmitter<any>();
  @Output() onOpen = new EventEmitter<any>();

  @ViewChild('trigger') trigger: any;
  @ViewChild('container') container: ElementRef;

  // Store event handler references for proper cleanup
  private closeHandler = (event: Event) => this.close(event);
  private showHandler = (event: Event) => this.show(event);

  constructor(private formBuilder: FormBuilder,) {
    bindFunctionsToThis(this);
    this.formGroup = this.formBuilder.group({
      'input': [null, [Validators.required]],
    });
  }

  public ngAfterViewInit() {
    setTimeout(() => {
      this.bindEvents();
      this.value = this.value.filter(x => isNullOrUndefined(x.hide) || x.hide === false);
      let selected = this.value.first(x => x.selected);
      if (selected) {
        this.fieldValue = selected.text;
      }
    }, 500);
  }

  public get value() {
    return this._value ? this._value.filter(x => !!x) : undefined;
  }

  public set value(newValue: any) {
    if (newValue === this._value || (isNullOrUndefined(this._value) && isNullOrUndefined(newValue))) {
      return;
    }

    this._value = newValue;

    if (this._notifyChange) {
      this._notifyChange(newValue);
    }
  }

  public writeValue(newValue: any): void {
    this._value = newValue;
  }

  public registerOnChange(fn: any): void {
    this._notifyChange = fn;
  }

  public registerOnTouched(fn: any): void {

  }

  public ngOnDestroy() {
    this.unbindEvents();
  }

  public setSelected(value) {
    let selected = this.value.first(x => x.value === value);
    if (selected) {
      this.value.forEach(x => x.selected = false);
      selected.selected = true;
      this.fieldValue = selected.text;
    }
  }

  public select(value) {
    this.itemSelected.emit(value);
    this.fieldValue = value.text;
    this.trigger.input.nativeElement.focus();
    this.close();
  }

  private bindEvents() {
    window.addEventListener('click', this.closeHandler);
    // Attach click listener to the entire field wrapper, not just the input element
    if (this.trigger && this.trigger.field) {
      const fieldElement = this.trigger.field._elementRef.nativeElement;
      fieldElement.addEventListener('click', this.showHandler);
    }
  }

  private unbindEvents() {
    window.removeEventListener('click', this.closeHandler);
    // Remove click listener from the entire field wrapper
    if (this.trigger && this.trigger.field) {
      const fieldElement = this.trigger.field._elementRef.nativeElement;
      fieldElement.removeEventListener('click', this.showHandler);
    }
  }

  public close(event?: Event) {
    if (!event) {
      this.isShowing = false;
      return;
    }

    const target = event.target as any;
    
    // Check if click is inside the dropdown container
    let parentNode = target.parentNode;
    while (parentNode != null && parentNode !== this.container.nativeElement) {
      parentNode = parentNode.parentNode;
    }
    const isInsideContainer = parentNode === this.container.nativeElement;
    
    // Check if click is inside the trigger field wrapper
    let triggerParent = target;
    const triggerFieldElement = this.trigger?.field?._elementRef?.nativeElement;
    while (triggerParent != null && triggerParent !== triggerFieldElement && triggerParent !== document.body) {
      triggerParent = triggerParent.parentNode;
    }
    const isInsideTrigger = triggerParent === triggerFieldElement;
    
    // Don't close if click is inside container or trigger field
    // (trigger field clicks are handled by show handler for toggling)
    if (isInsideContainer || isInsideTrigger) {
      return;
    }
    
    // Close dropdown if click is outside both container and trigger
    this.isShowing = false;
  }

  private show(event: Event) {
    if (this.disabled) {
      return;
    }
    if (this.isShowing) {
      this.close();
      return;
    }
    if (this.showDropDownItem === 'false') {
      this.itemSelected.emit();
      return;
    }

    this.onOpen.emit();
    event && event.stopPropagation();
    let dropDownGap = this.label ? 18 : 10;
    let container = this.container.nativeElement;
    let containerRect = container.getBoundingClientRect();
    let triggerRect = this.trigger.input.nativeElement.getBoundingClientRect();
    let top = triggerRect.height + dropDownGap;
    let left = 0;

    if (this.place === 'top') {
      top = -containerRect.height - dropDownGap;
    }

    if (this.align === 'right') {
      left = triggerRect.right - containerRect.width;
    }

    this.isShowing = true;
    container.style.left = `${left}px`;
    container.style.top = `${top}px`;
  }

}
